import React, { useState, useEffect } from 'react';
import { FaFileAlt, FaDownload, FaCalendar, FaEye } from 'react-icons/fa';
import { useLanguage } from '../context/LanguageContext';
import { contentAPI } from '../services/api';
import Footer from '../components/Footer';
import LoadingSpinner from '../components/LoadingSpinner';
import PageCover from '../components/PageCover';

const CommuniquesPage = () => {
  const [communiques, setCommuniques] = useState([]);
  const [loading, setLoading] = useState(true);
  const { language } = useLanguage();

  useEffect(() => {
    loadCommuniques();
  }, []);

  const loadCommuniques = async () => {
    try {
      setLoading(true);
      const res = await contentAPI.getCommuniques();
      setCommuniques(res.data || res || []);
    } catch (error) {
      console.error('Erreur:', error);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return <LoadingSpinner />;
  }

  return (
    <div style={styles.container}>
      <PageCover
        title={language === 'ar' ? 'القوانين واللوائح' : 'Lois et Règlements'}
        subtitle={language === 'ar'
          ? 'الرابطة الوطنية لكرة القدم للهواة - المستوى الثاني'
          : 'Ligue Amateur de Football - Niveau 2'}
        icon={FaFileAlt}
      />

      {/* Communiques List */}
      <div style={styles.content}>
        <div style={styles.communiquesGrid}>
          {communiques.map((communique) => (
            <div key={communique.id} style={styles.communiqueCard}>
              <div style={styles.cardHeader}>
                <div style={styles.cardIcon}>
                  <FaFileAlt />
                </div>
                <div style={styles.cardHeaderInfo}>
                  <h2 style={styles.cardTitle}>
                    {language === 'ar' ? communique.title_ar : communique.title}
                  </h2>
                  <div style={styles.cardMeta}>
                    <span style={styles.metaItem}>
                      <FaCalendar style={styles.metaIcon} />
                      {new Date(communique.published_at || communique.date).toLocaleDateString(
                        language === 'ar' ? 'ar-TN' : 'fr-FR',
                        { year: 'numeric', month: 'long', day: 'numeric' }
                      )}
                    </span>
                    <span style={styles.metaItem}>
                      <FaEye style={styles.metaIcon} />
                      {communique.views} {language === 'ar' ? 'مشاهدة' : 'vues'}
                    </span>
                  </div>
                </div>
              </div>

              <div style={styles.cardBody}>
                <div style={styles.authorBadge}>
                  {communique.author}
                </div>
                <p style={styles.cardContent}>
                  {language === 'ar' ? communique.content_ar : communique.content}
                </p>
              </div>

              <div style={styles.cardFooter}>
                <button
                  style={styles.downloadBtn}
                  onClick={() => {
                    if (communique.pdf_url) {
                      window.open(communique.pdf_url.startsWith('http') ? communique.pdf_url : `${window.location.origin}${communique.pdf_url}`, '_blank');
                    }
                  }}
                >
                  <FaDownload style={styles.btnIcon} />
                  {language === 'ar' ? 'تحميل PDF' : 'Télécharger PDF'}
                </button>
              </div>
            </div>
          ))}
        </div>

        {/* Info Box */}
        <div style={styles.infoBox}>
          <h3 style={styles.infoTitle}>
            {language === 'ar' ? 'معلومات هامة' : 'Informations Importantes'}
          </h3>
          <p style={styles.infoText}>
            {language === 'ar'
              ? 'جميع القوانين واللوائح الصادرة عن الرابطة الوطنية لكرة القدم للهواة - المستوى الثاني. يرجى قراءة جميع النصوص بعناية والالتزام بالتعليمات الواردة فيها.'
              : 'Tous les textes officiels (lois et règlements) de la Ligue Amateur de Football - Niveau 2. Veuillez lire attentivement et respecter les instructions qui y figurent.'}
          </p>
          <div style={styles.contactInfo}>
            <p><strong>{language === 'ar' ? 'للاستفسار:' : 'Pour toute question:'}</strong></p>
            <p>📧 <a href="mailto:contact@ligue-amateur-n2.tn" style={styles.contactTel}>contact@ligue-amateur-n2.tn</a></p>
            <p>📞 <a href="tel:+21671232433" style={styles.contactTel} dir="ltr">+216 71 232 433</a></p>
          </div>
        </div>
      </div>

      <Footer />
    </div>
  );
};

const styles = {
  container: {
    minHeight: '100vh',
    background: '#f5f5f5',
    width: '100%',
    maxWidth: '100vw',
    overflowX: 'hidden'
  },
  content: {
    width: '100%',
    maxWidth: '1200px',
    margin: '0 auto',
    padding: '40px 16px 60px',
    boxSizing: 'border-box'
  },
  communiquesGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fill, minmax(min(280px, 100%), 1fr))',
    gap: '24px',
    marginBottom: '40px',
    width: '100%'
  },
  communiqueCard: {
    background: 'white',
    borderRadius: '15px',
    boxShadow: '0 4px 15px rgba(0,0,0,0.1)',
    overflow: 'hidden',
    transition: 'transform 0.3s, box-shadow 0.3s',
    cursor: 'pointer'
  },
  cardHeader: {
    display: 'flex',
    gap: '16px',
    padding: '20px',
    background: 'linear-gradient(135deg, #f8f9fa 0%, #e9ecef 100%)',
    borderBottom: '3px solid #1a365d',
    flexWrap: 'wrap'
  },
  cardIcon: {
    width: '48px',
    height: '48px',
    minWidth: '48px',
    minHeight: '48px',
    background: '#1a365d',
    borderRadius: '50%',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    color: 'white',
    fontSize: '28px',
    flexShrink: 0
  },
  cardHeaderInfo: {
    flex: 1
  },
  cardTitle: {
    fontSize: '22px',
    fontWeight: 'bold',
    color: '#333',
    marginBottom: '10px'
  },
  cardMeta: {
    display: 'flex',
    gap: '20px',
    flexWrap: 'wrap'
  },
  metaItem: {
    display: 'flex',
    alignItems: 'center',
    gap: '6px',
    fontSize: '14px',
    color: '#666'
  },
  metaIcon: {
    fontSize: '14px',
    color: '#1a365d'
  },
  cardBody: {
    padding: '25px'
  },
  authorBadge: {
    display: 'inline-block',
    background: '#1a365d',
    color: 'white',
    padding: '6px 15px',
    borderRadius: '20px',
    fontSize: '13px',
    fontWeight: '600',
    marginBottom: '15px'
  },
  cardContent: {
    fontSize: '16px',
    lineHeight: '1.8',
    color: '#555',
    textAlign: 'justify'
  },
  cardFooter: {
    padding: '20px 25px',
    background: '#f8f9fa',
    borderTop: '1px solid #e0e0e0'
  },
  downloadBtn: {
    display: 'flex',
    alignItems: 'center',
    gap: '10px',
    padding: '12px 25px',
    background: '#1a365d',
    color: 'white',
    border: 'none',
    borderRadius: '8px',
    fontSize: '15px',
    fontWeight: '600',
    cursor: 'pointer',
    transition: 'background 0.3s'
  },
  btnIcon: {
    fontSize: '16px'
  },
  infoBox: {
    background: 'white',
    padding: '30px',
    borderRadius: '15px',
    boxShadow: '0 4px 15px rgba(0,0,0,0.1)',
    borderLeft: '5px solid #1a365d'
  },
  infoTitle: {
    fontSize: '24px',
    fontWeight: 'bold',
    color: '#333',
    marginBottom: '15px'
  },
  infoText: {
    fontSize: '16px',
    lineHeight: '1.8',
    color: '#555',
    marginBottom: '20px'
  },
  contactInfo: {
    background: '#f8f9fa',
    padding: '20px',
    borderRadius: '10px',
    fontSize: '15px',
    lineHeight: '1.8'
  },
  contactTel: {
    color: '#1a365d',
    textDecoration: 'none',
    fontWeight: '600'
  }
};

export default CommuniquesPage;
